import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Upload,
  X,
  CheckCircle,
  MessageCircle,
  Trash2,
  ThumbsDown,
  AlertCircle,
  RefreshCw,
  Image as ImageIcon,
  Calendar,
  ShieldX,
  Plus,
  DollarSign,
  ListChecks,
} from 'lucide-react';
import { Complaint, ComplaintComment, SlaExtension, ViolationType } from '../types';
import apiService from '../services/api';
import { getTimeRemaining, formatDateTime, getStatusColor, getStatusText } from '../utils/timeUtils';
import PhotoUploadModal from '../components/complaints/PhotoUploadModal';
import LocationMap from '../components/common/LocationMap';
import SLACountdown from '../components/complaints/SLACountdown';
import ExtendSlaDialog from '../components/ExtendSlaDialog';

const getOptimizedImageUrl = (url: string, width = 800): string => {
  if (!url || !url.includes('/upload/')) return url;
  return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
};

const ComplaintDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = apiService.getCurrentUser();

  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [comments, setComments] = useState<ComplaintComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Photo upload modal
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Rework dialog
  const [reworkDialogOpen, setReworkDialogOpen] = useState(false);
  const [reworkNotes, setReworkNotes] = useState('');

  // Comment
  const [newComment, setNewComment] = useState('');

  // Image viewer dialog
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState('');

  // Extend SLA dialog
  const [extendSlaDialogOpen, setExtendSlaDialogOpen] = useState(false);

  // Admin close dialog
  const [adminCloseDialogOpen, setAdminCloseDialogOpen] = useState(false);

  // Add deduction from complaint dialog (admin only)
  const [addDeductionOpen, setAddDeductionOpen] = useState(false);
  const [deductionViolationTypes, setDeductionViolationTypes] = useState<ViolationType[]>([]);
  const [deductionVtLoading, setDeductionVtLoading] = useState(false);
  const [deductionForm, setDeductionForm] = useState({ violationTypeId: '', notes: '' });
  const [deductionSaving, setDeductionSaving] = useState(false);

  // Real-time time info state for chronometer
  const [timeInfo, setTimeInfo] = useState(() =>
    complaint ? getTimeRemaining(complaint.slaDeadline) : { isOverdue: false, hours: 0, minutes: 0, seconds: 0, text: '', percentage: 0 }
  );

  const fetchComplaint = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getComplaint(id!);
      setComplaint(data);
      setError('');
    } catch (err) {
      setError(t('complaintDetail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchComments = useCallback(async () => {
    try {
      const data = await apiService.getComplaintComments(id!);
      setComments(data);
    } catch (err) {
      // Failed to fetch comments
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchComplaint();
      fetchComments();
    }
  }, [id, fetchComplaint, fetchComments]);

  // Update time info every second for real-time chronometer
  useEffect(() => {
    if (!complaint) return;

    // Update immediately
    setTimeInfo(getTimeRemaining(complaint.slaDeadline));

    // Don't run timer for completed/closed complaints
    if (['resolved', 'closed', 'closed_by_admin'].includes(complaint.status)) return;

    // Set up interval to update every second
    const intervalId = setInterval(() => {
      setTimeInfo(getTimeRemaining(complaint.slaDeadline));
    }, 1000); // 1000ms = 1 second

    // Cleanup interval on unmount or when complaint changes
    return () => {
      clearInterval(intervalId);
    };
  }, [complaint]);

  const handleStartWork = async () => {
    if (!complaint) return;

    try {
      setActionLoading(true);
      await apiService.startWork(complaint.id);
      await fetchComplaint();
      setSuccess(t('complaintDetail.startWorkSuccess'));
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || t('complaintDetail.statusUpdateFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptComplaint = async () => {
    if (!complaint) return;

    try {
      setActionLoading(true);
      await apiService.acceptComplaint(complaint.id);
      await fetchComplaint();
      setSuccess(t('complaintDetail.acceptSuccess'));
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || t('complaintDetail.acceptFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminClose = async () => {
    if (!complaint) return;
    try {
      setActionLoading(true);
      await apiService.closeComplaintByAdmin(complaint.id);
      await fetchComplaint();
      setAdminCloseDialogOpen(false);
      setSuccess(isRTL ? 'تم إغلاق الشكوى من الإدارة' : 'Complaint closed by admin');
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || (isRTL ? 'فشل الإغلاق' : 'Failed to close complaint'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSla = async (data: {
    additionalHours: number;
    resetToFullCycle: boolean;
    justification: string;
  }) => {
    if (!complaint) return;

    try {
      await apiService.extendComplaintSla(complaint.id, data);
      await fetchComplaint();
      setSuccess('SLA extended successfully | تم تمديد الموعد بنجاح');
      setError('');
    } catch (err: any) {
      throw err;
    }
  };

  const handleRequestRework = async () => {
    if (!complaint || !reworkNotes.trim()) return;

    try {
      setActionLoading(true);
      await apiService.requestRework(complaint.id, reworkNotes);
      await fetchComplaint();
      setReworkDialogOpen(false);
      setReworkNotes('');
      setSuccess(t('complaintDetail.reworkSuccess'));
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || t('complaintDetail.reworkFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadPhotos = async (files: File[], location: GeolocationCoordinates) => {
    if (!complaint || files.length === 0) return;

    try {
      for (const file of files) {
        await apiService.uploadAfterPhoto(
          complaint.id,
          file,
          location.latitude,
          location.longitude
        );
      }
      await apiService.submitResolution(complaint.id);
      await fetchComplaint();
      setUploadDialogOpen(false);
      setSuccess(t('complaintDetail.uploadSuccess'));
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || t('complaintDetail.uploadFailed'));
      throw err;
    }
  };

  const handleAddComment = async () => {
    if (!complaint || !newComment.trim()) return;

    try {
      await apiService.createComplaintComment(complaint.id, newComment);
      await fetchComments();
      setNewComment('');
      setSuccess(t('complaintDetail.commentAddSuccess'));
    } catch (err: any) {
      setError(t('complaintDetail.commentAddFailed'));
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm(t('complaintDetail.confirmDeleteComment'))) return;

    try {
      await apiService.deleteComplaintComment(commentId);
      await fetchComments();
      setSuccess(t('complaintDetail.commentDeleteSuccess'));
    } catch (err: any) {
      setError(t('complaintDetail.commentDeleteFailed'));
    }
  };

  const handleImageClick = (url: string) => {
    setViewerImage(url);
    setViewerOpen(true);
  };

  const handleOpenAddDeduction = () => {
    if (!complaint) return;
    setDeductionForm({ violationTypeId: '', notes: '' });
    setDeductionViolationTypes([]);
    setDeductionVtLoading(true);
    const companyId = (complaint.company?._id || complaint.company?.id)?.toString() || '';
    apiService.getViolationTypes(companyId)
      .then((data) => setDeductionViolationTypes(data))
      .catch(() => setDeductionViolationTypes([]))
      .finally(() => setDeductionVtLoading(false));
    setAddDeductionOpen(true);
  };

  const handleSaveDeduction = async () => {
    if (!complaint || !deductionForm.violationTypeId) return;
    try {
      setDeductionSaving(true);
      const companyId = (complaint.company?._id || complaint.company?.id)?.toString() || '';
      const payload: any = {
        company: companyId,
        violationType: deductionForm.violationTypeId,
        complaint: complaint.id,
        notes: deductionForm.notes || undefined,
      };
      await apiService.createCompanyDeduction(payload);
      setAddDeductionOpen(false);
      setDeductionForm({ violationTypeId: '', notes: '' });
      setSuccess(isRTL ? 'تم إضافة الخصم بنجاح' : 'Deduction added successfully');
      setError('');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || (isRTL ? 'فشل إضافة الخصم' : 'Failed to add deduction')));
    } finally {
      setDeductionSaving(false);
    }
  };

  const getUserTypeLabel = (userType: string): string => {
    switch (userType) {
      case 'admin':
        return t('complaintDetail.userTypes.admin');
      case 'company_manager':
        return t('complaintDetail.userTypes.companyManager');
      case 'company_owner':
        return t('complaintDetail.userTypes.companyOwner');
      case 'evaluator':
        return t('complaintDetail.userTypes.evaluator');
      default:
        return userType;
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-orange-100 text-orange-800 border-orange-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      rework_requested: 'bg-red-100 text-red-800 border-red-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200',
      closed_by_admin: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return statusColors[status] || statusColors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-2xl p-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('complaintDetail.notFound')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const photos = complaint.photos || [];
  const beforePhotos = photos.filter((p) => p.photoType === 'before');
  const afterPhotos = photos.filter((p) => p.photoType === 'after');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/complaints')}
          className="flex items-center gap-2 mb-6 px-4 py-2 text-gray-700 hover:text-brand-700 font-semibold transition-colors"
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('complaintDetail.backToList')}</span>
        </button>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header with Gradient */}
          <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 px-8 py-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-800/20 rounded-full -ml-24 -mb-24"></div>

            <div className="relative">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-black text-white mb-4" dir={isRTL ? 'rtl' : 'ltr'}>
                    {complaint.description}
                  </h1>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border ${getStatusBadgeStyle(complaint.status)}`}>
                      {getStatusText(complaint.status)}
                    </span>
                    {complaint.reworkCount > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        {`${t('complaintDetail.reworkCount')}: ${complaint.reworkCount}`}
                      </span>
                    )}
                  </div>
                </div>

                {/* SLA Countdown */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 min-w-[280px]">
                  <SLACountdown
                    slaDeadline={complaint.slaDeadline}
                    complaintStatus={complaint.status}
                    variant="detailed"
                    showIcon={true}
                  />
                  {user?.userType === 'admin' && complaint.status !== 'closed' && complaint.status !== 'closed_by_admin' && (
                    <button
                      onClick={() => setExtendSlaDialogOpen(true)}
                      className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-bold rounded-xl transition-all duration-300"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>تمديد الموعد | Extend Deadline</span>
                    </button>
                  )}
                  {/* Admin Close button — visible directly in header */}
                  {user?.userType === 'admin' && complaint.status !== 'closed' && complaint.status !== 'closed_by_admin' && (
                    <button
                      onClick={() => setAdminCloseDialogOpen(true)}
                      className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-all duration-300"
                    >
                      <ShieldX className="w-4 h-4" />
                      <span>{isRTL ? 'إغلاق من الإدارة' : 'Close by Admin'}</span>
                    </button>
                  )}
                  {/* Add Deduction button — admin only, below close button */}
                  {user?.userType === 'admin' && (
                    <button
                      onClick={handleOpenAddDeduction}
                      className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all duration-300"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{isRTL ? 'إضافة خصم' : 'Add Deduction'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="p-8">
            {/* Location and Time Info Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Location Card */}
              <div className="bg-gradient-to-br from-brand-50 to-emerald-50 rounded-2xl p-6 border border-brand-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-brand-600 rounded-xl">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.location')}
                  </h3>
                </div>
                <p className="text-gray-700 font-semibold mb-3" dir={isRTL ? 'rtl' : 'ltr'}>
                  {complaint.locationName}
                </p>
                {complaint.latitude && complaint.longitude && (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      {complaint.latitude.toFixed(6)}, {complaint.longitude.toFixed(6)}
                    </p>
                    <LocationMap
                      latitude={complaint.latitude}
                      longitude={complaint.longitude}
                      locationName={complaint.locationName}
                      height="200px"
                      zoom={15}
                    />
                  </>
                )}
              </div>

              {/* Time Info Card — hidden when complaint is finished */}
              {!['resolved', 'closed', 'closed_by_admin'].includes(complaint.status) && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-blue-600 rounded-xl">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.timeRemaining')}
                  </h3>
                </div>
                <p className={`text-2xl font-black mb-4 ${timeInfo.isOverdue ? 'text-red-600' : 'text-green-600'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                  {timeInfo.text}
                </p>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      timeInfo.isOverdue
                        ? 'bg-gradient-to-r from-red-500 to-red-600'
                        : timeInfo.percentage < 30
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                        : 'bg-gradient-to-r from-green-500 to-green-600'
                    }`}
                    style={{ width: `${Math.min(timeInfo.percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.createdAt')}: {formatDateTime(complaint.createdAt)}
                  </span>
                </div>
              </div>
              )}
            </div>

            {/* Rework Notes Warning */}
            {complaint.reworkNotes && (
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-2xl p-6 mb-8">
                <h4 className="text-lg font-bold text-amber-900 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('complaintDetail.reworkNotes')}:
                </h4>
                <p className="text-amber-800" dir={isRTL ? 'rtl' : 'ltr'}>
                  {complaint.reworkNotes}
                </p>
              </div>
            )}

            {/* Photos Section */}
            <div className="space-y-8 mb-8">
              {/* Before Photos */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  <ImageIcon className="w-6 h-6 text-brand-600" />
                  {t('complaintDetail.beforePhotos')} ({beforePhotos.length})
                </h3>
                {beforePhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {beforePhotos.map((photo) => (
                      <div
                        key={photo._id}
                        className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        onClick={() => handleImageClick(photo.photoUrl)}
                      >
                        <img
                          src={getOptimizedImageUrl(photo.photoUrl, 600)}
                          alt="Before"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.noPhotos')}
                  </p>
                )}
              </div>

              {/* After Photos */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  <ImageIcon className="w-6 h-6 text-green-600" />
                  {t('complaintDetail.afterPhotos')} ({afterPhotos.length})
                </h3>
                {afterPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {afterPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        onClick={() => handleImageClick(photo.photoUrl)}
                      >
                        <img
                          src={getOptimizedImageUrl(photo.photoUrl, 600)}
                          alt="After"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                          <p className="text-white text-xs font-semibold">
                            {formatDateTime(photo.createdAt)}
                          </p>
                          {photo.latitude && photo.longitude && (
                            <p className="text-white/80 text-xs flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {photo.latitude.toFixed(5)}, {photo.longitude.toFixed(5)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.noPhotos')}
                  </p>
                )}
              </div>
            </div>

            {/* SLA Extension History */}
            {complaint.slaExtensions && complaint.slaExtensions.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <RefreshCw className="w-6 h-6 text-brand-600" />
                  سجل تمديد مواعيد الإصلاح | Extension History
                </h3>
                <div className="space-y-3">
                  {complaint.slaExtensions.map((extension: SlaExtension, index: number) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-lg">
                          Extension #{index + 1}
                        </span>
                        <span className="text-sm text-gray-600">
                          {formatDateTime(extension.extendedAt)}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-600 mb-1">Additional Hours:</p>
                          <p className="text-lg font-bold text-gray-800">{extension.additionalHours}h</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-600 mb-1">Type:</p>
                          <p className="text-lg font-bold text-gray-800">
                            {extension.resetToFullCycle ? 'Full Reset' : 'Added Hours'}
                          </p>
                        </div>
                      </div>
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-600 mb-1">Justification:</p>
                        <p className="text-gray-700">{extension.justification}</p>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Previous: {formatDateTime(extension.previousDeadline)}</span>
                        <span>→</span>
                        <span className="font-semibold text-green-700">New: {formatDateTime(extension.newDeadline)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            {user?.userType !== 'admin' && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  <MessageCircle className="w-6 h-6 text-brand-600" />
                  {t('complaintDetail.comments')} ({comments.length})
                </h3>
                <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 max-h-96 overflow-y-auto mb-4">
                  {comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {comment.user.firstName.charAt(0)}
                              </div>
                              <div>
                                <p className="font-bold text-gray-800">
                                  {comment.user.firstName} {comment.user.lastName}
                                </p>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  comment.user.userType === 'admin'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-brand-100 text-brand-700'
                                }`}>
                                  {getUserTypeLabel(comment.user.userType)}
                                </span>
                              </div>
                            </div>
                            {user?.id === comment.user.id && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                            {comment.comment}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(comment.createdAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('complaintDetail.noComments')}
                    </p>
                  )}
                </div>
                <div className="flex gap-3">
                  <textarea
                    placeholder={t('complaintDetail.commentPlaceholder')}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    rows={2}
                    className="flex-1 px-4 py-3 bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 transition-all duration-300 font-medium resize-none"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('complaintDetail.addComment')}
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              {/* Company Manager/Owner Actions */}
              {(user?.userType === 'company_manager' || user?.userType === 'company_owner') &&
                (complaint.status === 'pending' || complaint.status === 'rework_requested') && (
                <button
                  onClick={handleStartWork}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {actionLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  <span>{t('complaintDetail.startWork')}</span>
                </button>
              )}

              {(user?.userType === 'company_manager' || user?.userType === 'company_owner') &&
                complaint.status === 'in_progress' && (
                <button
                  onClick={() => setUploadDialogOpen(true)}
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <Upload className="w-5 h-5" />
                  <span>{t('complaintDetail.uploadAndFinish')}</span>
                </button>
              )}

              {/* Evaluator Actions */}
              {user?.userType === 'evaluator' && complaint.status === 'resolved' && (
                <>
                  <button
                    onClick={handleAcceptComplaint}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    {actionLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    <span>{t('complaintDetail.acceptComplaint')}</span>
                  </button>
                  <button
                    onClick={() => setReworkDialogOpen(true)}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 font-bold rounded-2xl transition-all duration-300"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <ThumbsDown className="w-5 h-5" />
                    <span>{t('complaintDetail.requestRework')}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Rework Request Dialog */}
        {reworkDialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full animate-scaleIn">
              {/* Dialog Header */}
              <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('complaintDetail.reworkDialogTitle')}
                  </h3>
                  <button
                    onClick={() => setReworkDialogOpen(false)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              {/* Dialog Body */}
              <div className="p-8">
                <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('complaintDetail.reworkNotesLabel') || (isRTL ? 'ملاحظات إعادة العمل' : 'Rework Notes')}
                </label>
                <textarea
                  placeholder={t('complaintDetail.reworkPlaceholder')}
                  value={reworkNotes}
                  onChange={(e) => setReworkNotes(e.target.value)}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 focus:bg-white transition-all font-medium resize-none"
                />
              </div>
              {/* Dialog Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={() => setReworkDialogOpen(false)}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleRequestRework}
                  disabled={!reworkNotes.trim() || actionLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    t('complaintDetail.submitRework')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo Upload Modal */}
        <PhotoUploadModal
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUpload={handleUploadPhotos}
          complaintId={complaint.id}
          isRTL={isRTL}
        />

        {/* Image Viewer Dialog */}
        {viewerOpen && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setViewerOpen(false)}>
            <button
              onClick={() => setViewerOpen(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={getOptimizedImageUrl(viewerImage, 1400)}
              alt="Full view"
              className="max-w-full max-h-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Admin Close Confirmation Dialog */}
        {adminCloseDialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full animate-scaleIn">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-6 rounded-t-3xl flex items-center justify-between">
                <h3 className="text-xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'إغلاق الشكوى من الإدارة' : 'Close Complaint by Admin'}
                </h3>
                <button onClick={() => setAdminCloseDialogOpen(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all">
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
              <div className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-purple-100 rounded-xl flex-shrink-0">
                    <ShieldX className="w-6 h-6 text-purple-700" />
                  </div>
                  <p className="text-gray-700 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL
                      ? 'هل أنت متأكد من إغلاق هذه الشكوى؟ سيتم تمييزها كـ "مغلقة من الإدارة" وليس كشكوى تم حلها من قبل الشركة.'
                      : 'Are you sure you want to close this complaint? It will be marked as "Closed by Admin", not as resolved by the company.'}
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={() => setAdminCloseDialogOpen(false)}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={handleAdminClose}
                  disabled={actionLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <ShieldX className="w-5 h-5" />}
                  <span>{isRTL ? 'تأكيد الإغلاق' : 'Confirm Close'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Deduction Dialog (admin only, pre-filled with company & complaint) */}
        {addDeductionOpen && complaint && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'إضافة خصم' : 'Add Deduction'}
                  </h3>
                  <button
                    onClick={() => setAddDeductionOpen(false)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              <div className="p-8 space-y-6">
                {/* Company — pre-filled, read-only */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'الشركة' : 'Company'}
                  </label>
                  <div className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    <span className="text-base">{complaint.company?.companyName || '—'}</span>
                    <span className="ml-auto text-xs text-gray-400 font-normal">{isRTL ? 'محدد تلقائياً' : 'auto-filled'}</span>
                  </div>
                </div>

                {/* Complaint — pre-filled, read-only */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'الشكوى' : 'Complaint'}
                  </label>
                  <div className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    <span className="text-sm line-clamp-2">{complaint.description?.substring(0, 80) || `#${complaint.id}`}</span>
                    <span className="ml-auto text-xs text-gray-400 font-normal flex-shrink-0">{isRTL ? 'محدد تلقائياً' : 'auto-filled'}</span>
                  </div>
                </div>

                {/* Violation Type Select */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'نوع الخطية' : 'Violation Type'} <span className="text-red-500">*</span>
                  </label>
                  {deductionVtLoading ? (
                    <div className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-gray-500 text-sm">{isRTL ? 'جاري التحميل...' : 'Loading...'}</span>
                    </div>
                  ) : deductionViolationTypes.length === 0 ? (
                    <div className="w-full px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-700 text-sm font-medium flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      <ListChecks className="w-4 h-4" />
                      {isRTL ? 'لا توجد أنواع خطية لهذه الشركة' : 'No violation types for this company'}
                    </div>
                  ) : (
                    <select
                      value={deductionForm.violationTypeId}
                      onChange={(e) => setDeductionForm({ ...deductionForm, violationTypeId: e.target.value })}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-100 focus:bg-white transition-all outline-none font-medium"
                    >
                      <option value="">{isRTL ? 'اختر نوع الخطية' : 'Select violation type'}</option>
                      {deductionViolationTypes.map((vt) => (
                        <option key={vt.id} value={vt.id}>
                          {vt.name} — {vt.price.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Auto-filled price preview */}
                  {deductionForm.violationTypeId && (() => {
                    const vt = deductionViolationTypes.find((v) => v.id === deductionForm.violationTypeId);
                    return vt ? (
                      <div className="mt-2 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div dir={isRTL ? 'rtl' : 'ltr'}>
                          <p className="text-xs text-gray-500 font-medium">{isRTL ? 'السعر' : 'Price'}</p>
                          <p className="text-lg font-black text-amber-700">
                            {vt.price.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* Notes (optional) */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'ملاحظات' : 'Notes'}
                    <span className="text-xs text-gray-400 font-normal ml-2">({isRTL ? 'اختياري' : 'optional'})</span>
                  </label>
                  <textarea
                    value={deductionForm.notes}
                    onChange={(e) => setDeductionForm({ ...deductionForm, notes: e.target.value })}
                    rows={3}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    placeholder={isRTL ? 'ملاحظات إضافية...' : 'Additional notes...'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-100 focus:bg-white transition-all outline-none font-medium resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={() => setAddDeductionOpen(false)}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={handleSaveDeduction}
                  disabled={!deductionForm.violationTypeId || deductionSaving}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deductionSaving ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span>{isRTL ? 'إضافة الخصم' : 'Add Deduction'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Extend SLA Dialog */}
        <ExtendSlaDialog
          open={extendSlaDialogOpen}
          onClose={() => setExtendSlaDialogOpen(false)}
          onSubmit={handleExtendSla}
          currentSlaHours={complaint.slaHours || 24}
        />
      </div>
    </div>
  );
};

export default ComplaintDetailPage;
